import { motion } from "framer-motion";
import { imgUrl } from "@/lib/api";

const DEFAULT_MANIFESTO = {
  eyebrow: "The Manifesto",
  title: "Three rules from a two-stoplight town.",
  chapters: [
    {
      number: "01",
      title: "The Wellston Soil",
      text: "Every morning at dawn, crisp Kirby cucumbers are hand-harvested from Deep Fork riverbed soils and brined within 6 hours.",
      image:
        "https://images.unsplash.com/photo-1568584711271-6c929fb49b60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxjdWN1bWJlciUyMGRpbGwlMjBoZXJicyUyMGZhcm18ZW58MHx8fHwxNzg3MTU2MzQwfDA&ixlib=rb-4.1.0&q=85",
    },
    {
      number: "02",
      title: "The 21-Day Cold Steep",
      text: "No industrial quick-boiling. We slow-infuse raw apple cider vinegar, coriander, peppercorn, and wild dill under heavy stone weights.",
      image:
        "https://images.unsplash.com/photo-1576325782957-363cfe40a6d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxydXN0aWMlMjBmYXJtJTIwa2l0Y2hlbiUyMHBhbnRyeSUyMHdvb2RlbnxlbnwwfHx8fDE3ODcxNTYzNDB8MA&ixlib=rb-4.1.0&q=85",
    },
    {
      number: "03",
      title: "The Audible Snap",
      text: "If a jar doesn't produce an echo in your jaw on the first bite, it never leaves our farm table.",
      image:
        "https://images.unsplash.com/photo-1610602925036-1d81bb50065a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxwaWNrbGUlMjBqYXIlMjBob21lbWFkZXxlbnwwfHx8fDE3ODcxNTYzMzB8MA&ixlib=rb-4.1.0&q=85",
    },
  ],
};

export const Manifesto = ({ content }) => {
  const eyebrow = content?.eyebrow ?? DEFAULT_MANIFESTO.eyebrow;
  const title = content?.title ?? DEFAULT_MANIFESTO.title;
  const chapters = DEFAULT_MANIFESTO.chapters.map((d, i) => ({
    ...d,
    ...((content?.chapters || [])[i] || {}),
  }));

  return (
    <section id="story" className="py-12 sm:py-16" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-10"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra font-semibold mb-3">
            {eyebrow}
          </p>
          <h2 className="font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-dill leading-tight">
            {title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {chapters.map((ch, i) => (
            <motion.article
              key={ch.number + i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group"
              data-testid={`manifesto-chapter-${ch.number}`}
            >
              <div className="relative overflow-hidden rounded-xl mb-5 aspect-[4/3]">
                <img
                  src={imgUrl(ch.image)}
                  alt={ch.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <span className="absolute top-3 left-3 font-serif font-black text-3xl text-outline-cream drop-shadow-lg">
                  {ch.number}
                </span>
              </div>
              <span className="font-serif font-black text-4xl text-outline block leading-none mb-2">
                {ch.number}
              </span>
              <h3 className="font-serif font-semibold text-lg sm:text-xl text-dill mb-2">{ch.title}</h3>
              <p className="text-sm text-ink/65 leading-relaxed">{ch.text}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
